/**
 * The logic behind <smart-grid> (components/shared/smart-grid.js): a
 * spreadsheet-like grid whose column headers sort, group and filter, kept
 * apart from the component so it can be tested. Nothing here knows about any
 * one page; a page describes its columns and the grid does the rest.
 */

/**
 * @typedef {Object} GridOption
 * @property {string} value
 * @property {string} label
 */

/**
 * @typedef {Object} GridBucket - the value a row is grouped and filtered by
 * @property {string} key - '' for "none"
 * @property {string} label
 */

/**
 * @typedef {Object} GridColumn
 * @property {string} key
 * @property {string} label
 * @property {(row: any) => any} value - the raw value: what an editor edits, and what sorts and groups when no bucket is given
 * @property {(row: any) => any} [display] - what the cell shows (a string or a lit template); the bucket's label by default
 * @property {(row: any) => GridBucket} [bucket] - what the row groups and filters under in this column
 * @property {GridOption[]} [options] - the column's values, in their natural order: they label and order buckets
 * @property {'text'|'select'} [editor] - how the cell edits, typed in or picked from a list of choices; not editable when omitted
 * @property {GridOption[]} [editOptions] - a select editor's choices, when they differ from options
 * @property {(row: any) => boolean} [editable] - whether this row's cell edits (default: every row)
 * @property {string|((row: any) => string)} [readonlyTitle] - the tooltip on a cell that doesn't edit
 * @property {'options'|'label'} [order] - how values sort and groups order: by options order, or alphabetically. Default: options when given
 * @property {boolean} [sortable] - default true
 * @property {boolean} [groupable] - default true when the column has options or a bucket
 * @property {boolean} [filterable] - default: groupable
 * @property {boolean} [searchable] - whether the quick search looks in this column
 * @property {string} [emptyLabel] - the label of the '' bucket, e.g. 'Unassigned'
 * @property {() => Array<GridBucket & {data?: any}>} [allGroups] - groups shown even when empty, in order, when grouping by this column unfiltered
 * @property {string} [className] - a class on the column's cells
 * @property {string} [width] - a CSS width for the column
 * @property {boolean} [elide] - the column that gives way: it takes the room the others leave and cuts its text short with an ellipsis (the full text shows on hover), so the grid doesn't scroll sideways. Set it on one column at most
 */

/**
 * @typedef {Object} GridState
 * @property {{key: string, dir: 'asc'|'desc'}|null} sort
 * @property {string} group - the grouped column's key, or ''
 * @property {Record<string, string[]>} filters - per column key, the bucket keys shown
 * @property {string} query - quick search text
 */

/** @returns {GridState} */
export function emptyGridState() {
    return { sort: null, group: '', filters: {}, query: '' };
}

/**
 * @param {GridColumn} col
 * @returns {boolean}
 */
export function isGroupable(col) {
    return col.groupable ?? Boolean(col.options || col.bucket);
}

/**
 * @param {GridColumn} col
 * @returns {boolean}
 */
export function isFilterable(col) {
    return col.filterable ?? isGroupable(col);
}

/**
 * @param {GridColumn} col
 * @returns {boolean}
 */
export function isSortable(col) {
    return col.sortable ?? true;
}

/**
 * The bucket a row falls in for a column.
 * @param {GridColumn} col
 * @param {any} row
 * @returns {GridBucket}
 */
export function bucketOf(col, row) {
    if (col.bucket) return col.bucket(row);
    const raw = col.value(row);
    const key = raw == null ? '' : String(raw);
    if (key === '') return { key: '', label: col.emptyLabel || '—' };
    const option = col.options?.find((o) => o.value === key);
    return { key, label: option ? option.label : key };
}

/**
 * A rank for a bucket: its place in options, alphabetical otherwise; the ''
 * bucket always last.
 * @param {GridColumn} col
 * @returns {(a: GridBucket, b: GridBucket) => number}
 */
function bucketComparator(col) {
    const byOptions =
        (col.order ?? (col.options ? 'options' : 'label')) === 'options' && col.options;
    const index = byOptions ? new Map(col.options.map((o, i) => [o.value, i])) : null;
    return (a, b) => {
        if (a.key === '' || b.key === '') return (a.key === '' ? 1 : 0) - (b.key === '' ? 1 : 0);
        if (index) {
            const d =
                (index.get(a.key) ?? Number.MAX_SAFE_INTEGER) -
                (index.get(b.key) ?? Number.MAX_SAFE_INTEGER);
            if (d) return d;
        }
        return a.label.localeCompare(b.label, undefined, { sensitivity: 'base', numeric: true });
    };
}

/**
 * Rows passing every column filter and the quick search.
 * @param {any[]} rows
 * @param {GridColumn[]} columns
 * @param {GridState} state
 * @returns {any[]}
 */
export function filterRows(rows, columns, state) {
    const active = Object.entries(state.filters || {})
        .filter(([, keys]) => keys?.length)
        .map(([key, keys]) => ({ col: columns.find((c) => c.key === key), keys: new Set(keys) }))
        .filter((f) => f.col);
    const q = (state.query || '').trim().toLowerCase();
    const searchable = columns.filter((c) => c.searchable);
    return rows.filter((row) => {
        for (const f of active) {
            if (!f.keys.has(bucketOf(f.col, row).key)) return false;
        }
        if (
            q &&
            !searchable.some((c) =>
                String(c.value(row) ?? '')
                    .toLowerCase()
                    .includes(q),
            )
        ) {
            return false;
        }
        return true;
    });
}

/**
 * A sorted copy of the rows. Unsorted, they keep the order given (the page's
 * own order, e.g. a manual one). Ties fall back to the first column.
 * @param {any[]} rows
 * @param {GridColumn[]} columns
 * @param {GridState['sort']} sort
 * @returns {any[]}
 */
export function sortRows(rows, columns, sort) {
    const tiebreak = columns[0];
    const byTiebreak = tiebreak ? comparatorFor(tiebreak) : () => 0;
    const col = sort && columns.find((c) => c.key === sort.key);
    if (!col) return [...rows];
    const compare = comparatorFor(col);
    const sign = sort.dir === 'desc' ? -1 : 1;
    return [...rows].sort((a, b) => sign * compare(a, b) || byTiebreak(a, b));
}

/**
 * @param {GridColumn} col
 * @returns {(a: any, b: any) => number}
 */
function comparatorFor(col) {
    if (col.options || col.bucket) {
        const compare = bucketComparator(col);
        return (a, b) => compare(bucketOf(col, a), bucketOf(col, b));
    }
    return (a, b) => {
        const va = col.value(a);
        const vb = col.value(b);
        if (va == null || va === '') return vb == null || vb === '' ? 0 : 1;
        if (vb == null || vb === '') return -1;
        return String(va).localeCompare(String(vb), undefined, {
            sensitivity: 'base',
            numeric: true,
        });
    };
}

/**
 * @typedef {Object} GridGroup
 * @property {string} key
 * @property {string} label
 * @property {any[]} rows
 * @property {any} [data] - what the column's allGroups attached
 */

/**
 * Group rows by a column, keeping their order within each group. Groups come
 * in the column's order with the '' group last. When includeEmpty, the
 * column's allGroups are shown even with no rows.
 * @param {any[]} rows
 * @param {GridColumn[]} columns
 * @param {string} groupKey - '' for one group of everything
 * @param {{includeEmpty?: boolean}} [opts]
 * @returns {GridGroup[]}
 */
export function groupRows(rows, columns, groupKey, opts = {}) {
    const col = groupKey && columns.find((c) => c.key === groupKey);
    if (!col) return [{ key: '', label: '', rows }];
    /** @type {Map<string, GridGroup>} */
    const groups = new Map();
    if (opts.includeEmpty && col.allGroups) {
        for (const g of col.allGroups())
            groups.set(g.key, { key: g.key, label: g.label, rows: [], data: g.data });
    }
    for (const row of rows) {
        const b = bucketOf(col, row);
        if (!groups.has(b.key)) groups.set(b.key, { key: b.key, label: b.label, rows: [] });
        groups.get(b.key).rows.push(row);
    }
    const ordered = [...groups.values()];
    if (opts.includeEmpty && col.allGroups) {
        // allGroups already gives the order; anything else follows, '' last.
        const known = new Map(col.allGroups().map((g, i) => [g.key, i]));
        const compare = bucketComparator(col);
        return ordered.sort((a, b) => {
            const ia = known.has(a.key) ? known.get(a.key) : Number.MAX_SAFE_INTEGER - 1;
            const ib = known.has(b.key) ? known.get(b.key) : Number.MAX_SAFE_INTEGER - 1;
            return ia - ib || compare(a, b);
        });
    }
    return ordered.sort(bucketComparator(col));
}

/**
 * The values a column's filter offers: every bucket among the rows, with how
 * many rows each holds, in the column's order.
 * @param {any[]} rows
 * @param {GridColumn} col
 * @returns {Array<GridBucket & {count: number}>}
 */
export function distinctBuckets(rows, col) {
    /** @type {Map<string, GridBucket & {count: number}>} */
    const seen = new Map();
    for (const row of rows) {
        const b = bucketOf(col, row);
        const entry = seen.get(b.key) || { ...b, count: 0 };
        entry.count++;
        seen.set(b.key, entry);
    }
    return [...seen.values()].sort(bucketComparator(col));
}

/**
 * The state after toggling one value of a column's filter.
 * @param {GridState} state
 * @param {string} colKey
 * @param {string} bucketKey
 * @returns {GridState}
 */
export function toggleFilterValue(state, colKey, bucketKey) {
    const current = new Set(state.filters[colKey] || []);
    if (current.has(bucketKey)) current.delete(bucketKey);
    else current.add(bucketKey);
    const filters = { ...state.filters };
    if (current.size) filters[colKey] = [...current];
    else delete filters[colKey];
    return { ...state, filters };
}

/**
 * The neighbours rows have after moving them, together, within a group's
 * rows: placed before or after the target row, or at the top when there's no
 * target (an empty group, or a drop on the group's header).
 * @param {any[]} rows - the group's rows, in the order shown
 * @param {(row: any) => string} rowKey
 * @param {string|string[]} movingKeys - the row, or rows, moving
 * @param {string|null} targetKey
 * @param {'before'|'after'} placement
 * @returns {{prev: any|null, next: any|null}}
 */
export function neighboursAfterMove(rows, rowKey, movingKeys, targetKey, placement) {
    const moving = new Set([movingKeys].flat());
    const others = rows.filter((r) => !moving.has(rowKey(r)));
    let index = 0;
    if (targetKey != null) {
        const t = others.findIndex((r) => rowKey(r) === targetKey);
        index = t === -1 ? others.length : placement === 'after' ? t + 1 : t;
    }
    return { prev: others[index - 1] ?? null, next: others[index] ?? null };
}

/**
 * Whether a row can move into a group of the grouped column: only by editing
 * that column's value, so the column must edit on this row and offer the
 * group's value.
 * @param {GridColumn} col
 * @param {any} row
 * @param {string} groupKey
 * @returns {boolean}
 */
export function canMoveToGroup(col, row, groupKey) {
    if (bucketOf(col, row).key === groupKey) return true;
    if (!col.editor || (col.editable && !col.editable(row))) return false;
    return (col.editOptions || col.options || []).some((o) => o.value === groupKey);
}

/**
 * The cell keyboard navigation moves to, or null for a key it doesn't handle.
 * Arrows stop at the edges; Tab and Shift+Tab wrap across rows.
 * @param {{row: number, col: number}} pos
 * @param {string} key - KeyboardEvent.key
 * @param {number} rowCount
 * @param {number} colCount
 * @param {boolean} [shift]
 * @returns {{row: number, col: number}|null}
 */
export function nextCell(pos, key, rowCount, colCount, shift = false) {
    const { row, col } = pos;
    switch (key) {
        case 'ArrowUp':
            return { row: Math.max(0, row - 1), col };
        case 'ArrowDown':
            return { row: Math.min(rowCount - 1, row + 1), col };
        case 'ArrowLeft':
            return { row, col: Math.max(0, col - 1) };
        case 'ArrowRight':
            return { row, col: Math.min(colCount - 1, col + 1) };
        case 'Tab': {
            const index = row * colCount + col + (shift ? -1 : 1);
            if (index < 0 || index >= rowCount * colCount) return null;
            return { row: Math.floor(index / colCount), col: index % colCount };
        }
        default:
            return null;
    }
}

/**
 * The next choice after `from` whose label starts with a letter, wrapping
 * round, for typing to jump in a list of choices; -1 when none does.
 * @param {GridOption[]} options
 * @param {number} from
 * @param {string} letter
 * @returns {number}
 */
export function nextOptionStartingWith(options, from, letter) {
    const want = letter.toLowerCase();
    for (let step = 1; step <= options.length; step++) {
        const i = (from + step) % options.length;
        if (options[i].label.toLowerCase().startsWith(want)) return i;
    }
    return -1;
}

/**
 * @typedef {Object} GridShortcut
 * @property {string[]} keys - the ways to do it, each a key combination joined with '+'
 * @property {string} does
 */

/**
 * The grid's help: its keyboard shortcuts and mouse gestures, grouped, with
 * the selecting and reordering ones only when the grid does those.
 * @param {{selectable?: boolean, reorderable?: boolean}} grid
 * @returns {{title: string, note?: string, items: GridShortcut[]}[]}
 */
export function gridShortcuts({ selectable = false, reorderable = false } = {}) {
    const groups = [
        {
            title: 'Moving around',
            items: [
                { keys: ['↑', '↓', '←', '→'], does: 'Move between cells' },
                { keys: ['Tab', 'Shift+Tab'], does: 'Next or previous cell' },
            ],
        },
        {
            title: 'Editing',
            items: [
                { keys: ['Double-click', 'Enter', 'F2'], does: 'Edit the cell' },
                { keys: ['Type'], does: 'Edit a text cell, starting with what you type' },
                { keys: ['Enter', 'Tab'], does: 'Save the edit (Tab moves on too)' },
                { keys: ['Esc'], does: 'Cancel the edit (so does clicking away)' },
                { keys: ['↑', '↓', 'Home', 'End'], does: 'Move through a list of choices' },
                { keys: ['Enter', 'Click'], does: 'Choose from the list' },
                { keys: ['A–Z'], does: 'Jump to a choice starting with that letter' },
            ],
            note: 'A cell with a lock can’t be edited here; hover it to see why.',
        },
    ];
    if (selectable) {
        groups.push({
            title: 'Selecting',
            items: [
                { keys: ['Space'], does: 'Select or unselect the row' },
                { keys: ['Shift+Click'], does: 'Select every row from the last one ticked' },
            ],
        });
    }
    if (reorderable) {
        groups.push({
            title: 'Reordering',
            items: [
                { keys: ['Drag ⋮⋮'], does: 'Move the row, or the selected rows' },
                {
                    keys: ['Alt+↑', 'Alt+↓'],
                    does: 'Move the row, or the selected rows, up or down',
                },
            ],
            note: 'Only while no column is sorted.',
        });
    }
    groups.push(
        {
            title: 'Columns',
            items: [{ keys: ['Click a header'], does: 'Sort, group or filter by that column' }],
        },
        {
            title: 'Help',
            items: [
                { keys: ['?'], does: 'Show this help' },
                { keys: ['Esc'], does: 'Close it' },
            ],
        },
    );
    return groups;
}

/**
 * The ids between two rows (inclusive), in the order shown, for shift-click.
 * @param {string[]} orderedIds
 * @param {string} anchorId
 * @param {string} targetId
 * @returns {string[]}
 */
export function rangeOfIds(orderedIds, anchorId, targetId) {
    const a = orderedIds.indexOf(anchorId);
    const b = orderedIds.indexOf(targetId);
    if (a === -1 || b === -1) return [targetId];
    return orderedIds.slice(Math.min(a, b), Math.max(a, b) + 1);
}

/** The widest a header menu gets, in px (20rem), and its gap from the viewport edge. */
const MENU_WIDTH = 320;
const MENU_MARGIN = 8;
/** Below this much room under its header, a menu opens upward when there's more room above. */
const MENU_MIN_ROOM = 240;

/**
 * Where a header menu goes, in viewport px, for the header's rect: under the
 * header, or over it when it's near the bottom of the window, kept inside the
 * window, and no taller than the room it has.
 * @param {{ top: number, bottom: number, left: number }} rect
 * @param {{ innerWidth: number, innerHeight: number }} view
 * @returns {{ top: number | null, bottom: number | null, left: number, maxHeight: number }}
 */
export function menuPlacement(rect, view) {
    const left = Math.max(
        MENU_MARGIN,
        Math.min(rect.left, view.innerWidth - MENU_WIDTH - MENU_MARGIN),
    );
    const below = view.innerHeight - rect.bottom - MENU_MARGIN;
    const above = rect.top - MENU_MARGIN;
    if (below < MENU_MIN_ROOM && above > below) {
        return { top: null, bottom: view.innerHeight - rect.top, left, maxHeight: above };
    }
    return { top: rect.bottom, bottom: null, left, maxHeight: below };
}

/**
 * Whether a query string carries any grid state, as opposed to none at all.
 * @param {URLSearchParams} params
 * @returns {boolean}
 */
export function hasGridState(params) {
    return [...params.keys()].some(
        (k) => k === 'q' || k === 'sort' || k === 'group' || k.startsWith('f.'),
    );
}

/**
 * The query string for a grid state, leaving out what's unset or default.
 * Filters are `f.<column>=<key>,<key>`; the '' key is written as `~`.
 * @param {GridState} state
 * @param {GridState} [defaults]
 * @returns {string} - '' or '?…'
 */
export function serializeGridState(state, defaults = emptyGridState()) {
    const params = new URLSearchParams();
    if (state.query) params.set('q', state.query);
    const sameSort =
        (state.sort?.key ?? '') === (defaults.sort?.key ?? '') &&
        (state.sort?.dir ?? 'asc') === (defaults.sort?.dir ?? 'asc');
    if (!sameSort) {
        params.set('sort', state.sort ? `${state.sort.key}:${state.sort.dir}` : '');
    }
    if (state.group !== defaults.group) params.set('group', state.group);
    for (const [key, values] of Object.entries(state.filters)) {
        if (values?.length) params.set(`f.${key}`, values.map((v) => v || '~').join(','));
    }
    const qs = params.toString();
    return qs ? `?${qs}` : '';
}

/**
 * The grid state a query string describes, ignoring columns that don't
 * exist or can't do what it asks.
 * @param {URLSearchParams} params
 * @param {GridColumn[]} columns
 * @param {GridState} [defaults]
 * @returns {GridState}
 */
export function parseGridState(params, columns, defaults = emptyGridState()) {
    const byKey = new Map(columns.map((c) => [c.key, c]));
    let sort = defaults.sort;
    if (params.has('sort')) {
        const [key, dir] = (params.get('sort') || '').split(':');
        sort =
            byKey.has(key) && isSortable(byKey.get(key))
                ? { key, dir: dir === 'desc' ? 'desc' : 'asc' }
                : null;
    }
    let group = defaults.group;
    if (params.has('group')) {
        const key = params.get('group');
        group =
            key === '' || (byKey.has(key) && isGroupable(byKey.get(key))) ? key : defaults.group;
    }
    /** @type {Record<string, string[]>} */
    const filters = {};
    for (const [name, value] of params) {
        if (!name.startsWith('f.')) continue;
        const key = name.slice(2);
        if (!byKey.has(key) || !isFilterable(byKey.get(key)) || !value) continue;
        filters[key] = value.split(',').map((v) => (v === '~' ? '' : v));
    }
    return { sort, group, filters, query: params.get('q') || '' };
}
